import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Spinner, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { FaDog, FaPlus, FaSearch, FaTimes, FaUserCircle, FaSync } from "react-icons/fa";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";

export default function CustomerIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const AuthToken = localStorage.getItem("authToken");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Filter states - these are the temporary values from inputs
    const [tempFilters, setTempFilters] = useState({
        name: '',
        email: '',
        phone: '',
        per_page: 10
    });

    // Active filters - these are applied when search button is clicked
    const [activeFilters, setActiveFilters] = useState({
        name: '',
        email: '',
        phone: '',
        per_page: 10
    });

    // Pagination states
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    // Store Modal States
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        img: null
    });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({
        id: null,
        name: '',
        email: '',
        phone: '',
        address: '',
        img: null
    });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    // Breeds state
    const [breeds, setBreeds] = useState([]);

    // Fetch customers with active filters and pagination
    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.current_page,
                per_page: activeFilters.per_page,
                ...(activeFilters.name && { name: activeFilters.name }),
                ...(activeFilters.email && { email: activeFilters.email }),
                ...(activeFilters.phone && { phone: activeFilters.phone })
            });

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/customers?${params}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (res.data.success) {
                setData(res.data.customers.data || []);
                setBreeds(res.data.breeds || []);
                setPagination({
                    current_page: res.data.customers.current_page || 1,
                    last_page: res.data.customers.last_page || 1,
                    per_page: res.data.customers.per_page || 10,
                    total: res.data.customers.total || 0,
                    from: res.data.customers.from || 0,
                    to: res.data.customers.to || 0
                });
            }
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to fetch customers'));
        } finally {
            setLoading(false);
        }
    };

    // Fetch when active filters or pagination changes
    useEffect(() => {
        fetchCustomers();
    }, [activeFilters.name, activeFilters.email, activeFilters.phone, activeFilters.per_page, pagination.current_page]);

    // Handle filter input changes (temporary)
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Apply search - move temp filters to active filters
    const applySearch = () => {
        setActiveFilters({
            name: tempFilters.name,
            email: tempFilters.email,
            phone: tempFilters.phone,
            per_page: tempFilters.per_page
        });
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        const emptyFilters = {
            name: '',
            email: '',
            phone: '',
            per_page: 10
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        const value = parseInt(e.target.value);
        setTempFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setActiveFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setPagination(prev => ({
                ...prev,
                current_page: page
            }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle Enter key press in search inputs
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            applySearch();
        }
    };

    // Render Bootstrap Pagination
    const renderBootstrapPagination = () => {
        let items = [];
        let maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    {1}
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.current_page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.last_page) {
            if (endPage < pagination.last_page - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.last_page}
                    onClick={() => handlePageChange(pagination.last_page)}
                >
                    {pagination.last_page}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
            />
        );

        return items;
    };

    // ---------------- STORE ----------------
    const openStoreModal = () => {
        setStoreForm({ name: '', email: '', phone: '', address: '', img: null });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value, type, files } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: type === 'file' ? files[0] : value }));
        if (storeErrors[name]) setStoreErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        try {
            const formData = new FormData();
            for (let key in storeForm) {
                if (storeForm[key] !== null && storeForm[key] !== '') formData.append(key, storeForm[key]);
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/customers`, formData, {
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                fetchCustomers();
                setStoreModal(false);
                showSuccessToast(t('Customer added successfully!'));
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});
                showErrorToast(t('Please check the form for errors'));
            } else if (error.response?.status === 400) {
                setStoreErrors({ general: error.response.data.message });
                showErrorToast(error.response.data.message);
            } else {
                showErrorToast(t('Failed to add customer'));
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // ---------------- UPDATE ----------------
    const openUpdateModal = async (item) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/customers-edit/${item.id}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            const customer = res.data.customer;
            setUpdateForm({
                id: customer.id,
                name: customer.name,
                email: customer.email,
                phone: customer.phone,
                address: customer.address,
                img: null
            });
            setUpdateErrors({});
            setUpdateModal(true);
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to load customer details'));
        }
    };

    const handleUpdateChange = (e) => {
        const { name, value, type, files } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: type === 'file' ? files[0] : value }));
        if (updateErrors[name]) setUpdateErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        try {
            const formData = new FormData();
            for (let key in updateForm) {
                if (updateForm[key] !== null && updateForm[key] !== '') formData.append(key, updateForm[key]);
            }
            formData.append('_method', 'PUT');

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/customers-update/${updateForm.id}`, formData, {
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                fetchCustomers();
                setUpdateModal(false);
                showSuccessToast(t('Customer updated successfully!'));
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});
                showErrorToast(t('Please check the form for errors'));
            } else if (error.response?.status === 400) {
                setUpdateErrors({ general: error.response.data.message });
                showErrorToast(error.response.data.message);
            } else {
                showErrorToast(t('Failed to update customer'));
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // ---------------- DELETE ----------------
    const confirmDelete = (id) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await axios.delete(`${process.env.REACT_APP_API_URL}/customers/${id}`, {
                        headers: { Authorization: `Bearer ${AuthToken}` }
                    });
                    if (response.data.success) {
                        // Check if current page has no items after deletion
                        if (data.length === 1 && pagination.current_page > 1) {
                            setPagination(prev => ({ ...prev, current_page: pagination.current_page - 1 }));
                        } else {
                            fetchCustomers();
                        }
                        showSuccessToast(t('Customer deleted successfully!'));
                        return true;
                    }
                } catch (error) {
                    console.error(error);
                    showErrorToast(t('Failed to delete customer'));
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: t('Deleted!'),
                    text: t('Customer has been deleted.'),
                    icon: 'success',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        });
    };

    // ---------------- PETS MODAL ----------------
    const [petsModal, setPetsModal] = useState(false);
    const [petsData, setPetsData] = useState([]);
    const [petsLoading, setPetsLoading] = useState(true);
    const [currentPet, setCurrentPet] = useState(null);
    const [currentCustomerId, setCurrentCustomerId] = useState(null);
    const [petForm, setPetForm] = useState({
        id: null,
        name: '',
        breed_id: '',
        customer_id: '',
        age: '',
        color: '',
        weight: '',
        male: '',
        is_dead: false,
        birth_date: '',
        death_date: '',
    });
    const [petErrors, setPetErrors] = useState({});
    const [petBreeds, setPetBreeds] = useState([]);
    const [petCreateModal, setPetCreateModal] = useState(false);
    const [petCreateForm, setPetCreateForm] = useState({
        name: '',
        breed_id: '',
        customer_id: '',
        age: '',
        color: '',
        weight: '',
        male: '',
        is_dead: false,
        birth_date: '',
        death_date: '',
    });
    const [petCreateErrors, setPetCreateErrors] = useState({});
    const [petCreateLoading, setPetCreateLoading] = useState(false);

    const openPetsModal = async (customerId) => {
        setPetsLoading(true);
        setPetsModal(true);
        setCurrentCustomerId(customerId);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/customers-edit/${customerId}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            if (res.data.success) setPetsData(res.data.customer.pets || []);
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to load pets'));
        } finally {
            setPetsLoading(false);
        }
    };

    const openEditPet = async (petId) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets-edit/${petId}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            const pet = res.data.pet;
            setPetBreeds(res.data.breeds || []);
            setPetForm({
                id: pet.id,
                name: pet.name,
                breed_id: pet.breed_id,
                customer_id: pet.customer_id,
                age: pet.age || '',
                color: pet.color || '',
                weight: pet.weight || '',
                male: pet.male ? '1' : '0',
                is_dead: pet.is_dead || false,
                birth_date: pet.birth_date || '',
                death_date: pet.death_date || ''
            });
            setCurrentPet(pet.id);
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to load pet details'));
        }
    };

    const handlePetChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPetForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (petErrors[name]) setPetErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePetSubmit = async (e) => {
        e.preventDefault();
        setPetErrors({});

        const submitData = {
            ...petForm,
            male: parseInt(petForm.male)
        };

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/pets-update/${petForm.id}`, submitData, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            if (res.data.success) {
                setPetsData(prev => prev.map(p => p.id === petForm.id ? { ...p, ...res.data.data } : p));
                setCurrentPet(null);
                showSuccessToast(t('Pet updated successfully!'));
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setPetErrors(err.response.data.errors);
                showErrorToast(t('Please check the form for errors'));
            } else {
                showErrorToast(t('Failed to update pet'));
            }
            console.error(err);
        }
    };

    // ---------------- PET CREATE ----------------
    const openPetCreateModal = () => {
        setPetCreateForm({
            name: '',
            breed_id: '',
            customer_id: currentCustomerId,
            age: '',
            color: '',
            weight: '',
            male: '1',
            is_dead: false,
            birth_date: '',
            death_date: '',
        });
        setPetCreateErrors({});
        setPetCreateModal(true);
    };

    const handlePetCreateChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPetCreateForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (petCreateErrors[name]) setPetCreateErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handlePetCreateSubmit = async (e) => {
        e.preventDefault();
        setPetCreateLoading(true);
        setPetCreateErrors({});

        const submitData = {
            ...petCreateForm,
            male: parseInt(petCreateForm.male)
        };

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/pets`, submitData, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.success) {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/customers-edit/${currentCustomerId}`, {
                    headers: { Authorization: `Bearer ${AuthToken}` }
                });
                if (res.data.success) setPetsData(res.data.customer.pets || []);
                setPetCreateModal(false);
                showSuccessToast(t('Pet added successfully!'));
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setPetCreateErrors(error.response.data.errors || {});
                showErrorToast(t('Please check the form for errors'));
            } else {
                console.error(error);
                showErrorToast(t('Failed to add pet'));
            }
        } finally {
            setPetCreateLoading(false);
        }
    };

    // Helper function to format breed hierarchy
    const renderBreedOptions = (breedsList, level = 0) => {
        let options = [];
        breedsList.forEach(breed => {
            options.push(
                <option key={breed.id} value={breed.id} style={{ paddingLeft: `${level * 20}px` }}>
                    {"\u00A0".repeat(level * 4)}{breed.name}
                </option>
            );
            if (breed.child_breeds && breed.child_breeds.length > 0) {
                options.push(...renderBreedOptions(breed.child_breeds, level + 1));
            }
        });
        return options;
    };

    // Helper function to get gender label
    const getGenderLabel = (male) => {
        return male === 1 ? t('Male') : t('Female');
    };

    if (loading && !data.length) return <PageLayout><div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("loading")}...</span></div><p className="mt-3">{t("loading")}...</p></div></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('customers')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('customers')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('list')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('customer list')}</h5>
                            <ButtonComponent type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" onClick={openStoreModal}>
                                <FaPlus size={14} />
                                {t('add customer')}
                            </ButtonComponent>
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom bg-light">
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Name')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            size="sm"
                                            value={tempFilters.name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t("Search by name...")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Email')}</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            size="sm"
                                            value={tempFilters.email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t("Search by email...")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Phone')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="phone"
                                            size="sm"
                                            value={tempFilters.phone}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t("Search by phone...")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Per Page')}</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={tempFilters.per_page}
                                            onChange={handlePerPageChange}
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <div className="d-flex gap-2">
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={applySearch}
                                        >
                                            <FaSearch size={12} /> {t("Search")}
                                        </ButtonComponent>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-secondary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={clearFilters}
                                        >
                                            <FaTimes size={12} /> {t("Clear")}
                                        </ButtonComponent>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Results Info */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <small className="text-muted">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("customers")}
                            </small>
                            {loading && (
                                <small>
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </small>
                            )}
                        </div>

                        {/* Table - Using Bootstrap Table with global classes */}
                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '20%' }}>{t('name')}</th>
                                        <th style={{ width: '20%' }}>{t('email')}</th>
                                        <th style={{ width: '15%' }}>{t('phone')}</th>
                                        <th style={{ width: '25%' }}>{t('address')}</th>
                                        <th className="text-center" style={{ width: '15%' }}>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length > 0 ? (
                                        data.map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="text-center">
                                                   {item.id ?? '-'}
                                                </td>
                                                <td>
                                                    <strong className="text-primary">{item.name}</strong>
                                                </td>
                                                <td>{item.email || '-'}</td>
                                                <td>{item.phone || '-'}</td>
                                                <td>{item.address || '-'}</td>
                                                <td>
                                                    <div className="mc-table-action justify-content-center">
                                                        <ButtonComponent
                                                            className="material-icons edit"
                                                            onClick={() => openUpdateModal(item)}
                                                            title={t("Edit")}
                                                        >
                                                            edit
                                                        </ButtonComponent>
                                                        {/* <ButtonComponent 
                                                            className="material-icons delete" 
                                                            onClick={() => confirmDelete(item.id)}
                                                            title={t("Delete")}
                                                        >
                                                            delete
                                                        </ButtonComponent> */}
                                                        <ButtonComponent
                                                            className="material-icons view"
                                                            onClick={() => openPetsModal(item.id)}
                                                            title={t("View Pets")}
                                                        >
                                                            pets
                                                        </ButtonComponent>
                                                        <ButtonComponent
                                                            className="material-icons"
                                                            onClick={() => navigate(`/customers-profile/${item.id}`, { state: { activeTab: "customer", source: "customer-search" } })}
                                                            title={t("View Profile")}
                                                        >
                                                            visibility
                                                        </ButtonComponent>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5">
                                                <div className="empty-state">
                                                    <p className="text-muted mb-0">{t('No customers found')}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                </div>
                                <Pagination className="mb-0">
                                    {renderBootstrapPagination()}
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* STORE MODAL - Unchanged */}
            <Modal show={storeModal} onHide={() => setStoreModal(false)} size="lg" >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('add customer')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="name"
                                        value={storeForm.name}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.name}
                                        placeholder={t('customer name')}
                                    />
                                    <Form.Control.Feedback type="invalid">{storeErrors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('email')}</Form.Label>
                                    <Form.Control
                                        name="email"
                                        value={storeForm.email}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.email}
                                        placeholder={t('enter email address')}
                                    />
                                    <Form.Control.Feedback type="invalid">{storeErrors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('phone')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                name="phone"
                                value={storeForm.phone}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.phone}
                                placeholder={t('enter phone number')}
                            />
                            <Form.Control.Feedback type="invalid">{storeErrors.phone}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('address')}</Form.Label>
                            <Form.Control
                                name="address"
                                value={storeForm.address}
                                onChange={handleStoreChange}
                                as="textarea"
                                rows={2}
                                placeholder={t('enter address')}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('image')}</Form.Label>
                            <Form.Control type="file" name="img" onChange={handleStoreChange} accept="image/*" />
                        </Form.Group>
                        {storeErrors.general && <p className="text-danger">{storeErrors.general}</p>}
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            type="button" className="btn btn-secondary" onClick={() => setStoreModal(false)}>{t('cancel')}</ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={storeLoading}>
                            {storeLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('saving')}...</> : t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* UPDATE MODAL - Unchanged */}
            <Modal show={updateModal} onHide={() => setUpdateModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('edit customer')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="name"
                                        value={updateForm.name}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">{updateErrors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('email')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="email"
                                        value={updateForm.email}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">{updateErrors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('phone')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                name="phone"
                                value={updateForm.phone}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.phone}
                            />
                            <Form.Control.Feedback type="invalid">{updateErrors.phone}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('address')}</Form.Label>
                            <Form.Control
                                name="address"
                                value={updateForm.address}
                                onChange={handleUpdateChange}
                                as="textarea"
                                rows={2}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('image')}</Form.Label>
                            <Form.Control type="file" name="img" onChange={handleUpdateChange} accept="image/*" />
                        </Form.Group>
                        {updateErrors.general && <p className="text-danger">{updateErrors.general}</p>}
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent 
                            type="button" className="btn btn-secondary" onClick={() => setUpdateModal(false)}>{t('cancel')}</ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={updateLoading}>
                            {updateLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('updating')}...</> : t('update')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* PETS MODAL - Unchanged */}
            <Modal
                show={petsModal}
                onHide={() => setPetsModal(false)}
                size="lg"
                centered
                scrollable
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('customer pets')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="d-flex justify-content-end mb-3">
                        <ButtonComponent
                            className="btn btn-success btn-sm d-flex align-items-center gap-1"
                            onClick={openPetCreateModal}
                        >
                            <FaPlus size={12} />
                            {t('add pet')}
                        </ButtonComponent>
                    </div>

                    {petsLoading ? (
                        <div className="text-center my-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">{t('loading')}...</p>
                        </div>
                    ) : petsData.length === 0 ? (
                        <div className="text-center text-muted fs-5">{t('no pets found')}</div>
                    ) : (
                        petsData.map((pet) => (
                            <div
                                key={pet.id}
                                className={`border rounded p-3 mb-3 ${currentPet === pet.id ? 'border-primary shadow' : 'border-secondary'}`}
                                style={{ transition: 'box-shadow 0.3s ease', backgroundColor: '#f9f9f9' }}
                            >
                                {currentPet === pet.id ? (
                                    <Form onSubmit={handlePetSubmit}>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('name')} <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        name="name"
                                                        value={petForm.name}
                                                        onChange={handlePetChange}
                                                        isInvalid={!!petErrors.name}
                                                        placeholder={t('enter pet name')}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {petErrors.name}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('breed')} <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        name="breed_id"
                                                        value={petForm.breed_id}
                                                        onChange={handlePetChange}
                                                        isInvalid={!!petErrors.breed_id}
                                                    >
                                                        <option value="">{t('select breed')}</option>
                                                        {renderBreedOptions(breeds)}
                                                    </Form.Select>
                                                    <Form.Control.Feedback type="invalid">
                                                        {petErrors.breed_id}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('age')}</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="age"
                                                        value={petForm.age}
                                                        onChange={handlePetChange}
                                                        placeholder={t('enter age')}
                                                        min="0"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('color')}</Form.Label>
                                                    <Form.Control
                                                        name="color"
                                                        value={petForm.color}
                                                        onChange={handlePetChange}
                                                        placeholder={t('enter color')}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('weight')} (kg)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="weight"
                                                        value={petForm.weight}
                                                        onChange={handlePetChange}
                                                        placeholder={t('enter weight')}
                                                        min="0"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('birth date')}</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="birth_date"
                                                        value={petForm.birth_date}
                                                        onChange={handlePetChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('death date')}</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="death_date"
                                                        value={petForm.death_date}
                                                        onChange={handlePetChange}
                                                        disabled={!petForm.is_dead}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">
                                                        {t('gender')}
                                                    </Form.Label>

                                                    <Form.Select
                                                        name="male"
                                                        value={String(petForm.male)}
                                                        onChange={handlePetChange}
                                                    >
                                                        <option value="1">{t('Male')}</option>
                                                        <option value="0">{t('Female')}</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">{t('status')}</Form.Label>
                                                    <Form.Select
                                                        name="is_dead"
                                                        value={petForm.is_dead ? "1" : "0"}
                                                        onChange={(e) => {
                                                            const newValue = e.target.value === "1";
                                                            setPetForm(prev => ({ ...prev, is_dead: newValue }));
                                                            if (petErrors.is_dead) setPetErrors(prev => ({ ...prev, is_dead: '' }));
                                                        }}
                                                    >
                                                        <option value="0">{t('Alive')}</option>
                                                        <option value="1">{t('Deceased')}</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end gap-2">
                                            <ButtonComponent type="submit" className="btn btn-primary px-4">
                                                {t('save')}
                                            </ButtonComponent>
                                            <ButtonComponent
                                                type="button"
                                                className="btn btn-secondary px-4"
                                                onClick={() => setCurrentPet(null)}
                                            >
                                                {t('cancel')}
                                            </ButtonComponent>
                                        </div>
                                    </Form>
                                ) : (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <span className="fs-5 fw-semibold">{pet.name}</span>
                                            <small className="text-muted ms-2">({pet.breed?.name || '-'})</small>
                                            <span className="badge bg-info ms-2">{getGenderLabel(pet.male)}</span>
                                            {pet.weight && <span className="badge bg-secondary ms-2">{pet.weight} kg</span>}
                                        </div>
                                        <ButtonComponent
                                            className="btn btn-secondary px-4"
                                            onClick={() => openEditPet(pet.id)}
                                            aria-label={`Edit pet ${pet.name}`}
                                        >
                                            {t('edit')}
                                        </ButtonComponent>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </Modal.Body>

                <Modal.Footer className="p-2">
                    <ButtonComponent
                        className="btn btn-secondary px-4"
                        onClick={() => setPetsModal(false)}
                    >
                        {t('close')}
                    </ButtonComponent>
                </Modal.Footer>
            </Modal>

            {/* PET CREATE MODAL - Unchanged */}
            <Modal
                show={petCreateModal}
                onHide={() => setPetCreateModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('add new pet')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePetCreateSubmit}>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        name="name"
                                        value={petCreateForm.name}
                                        onChange={handlePetCreateChange}
                                        isInvalid={!!petCreateErrors.name}
                                        placeholder={t('enter pet name')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {petCreateErrors.name}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">{t('Weight')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="weight"
                                        value={petCreateForm.weight}
                                        onChange={handlePetCreateChange}
                                        placeholder={t('enter pet weight')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t('breed')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="breed_id"
                                        value={petCreateForm.breed_id}
                                        onChange={handlePetCreateChange}
                                        isInvalid={!!petCreateErrors.breed_id}
                                    >
                                        <option value="">{t('select breed')}</option>
                                        {renderBreedOptions(breeds)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {petCreateErrors.breed_id}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t('age')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="age"
                                        value={petCreateForm.age}
                                        onChange={handlePetCreateChange}
                                        placeholder={t('enter age')}
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t('color')}</Form.Label>
                                    <Form.Control
                                        name="color"
                                        value={petCreateForm.color}
                                        onChange={handlePetCreateChange}
                                        placeholder={t('enter color')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t('weight')} (kg)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="weight"
                                        value={petCreateForm.weight}
                                        onChange={handlePetCreateChange}
                                        placeholder={t('enter weight')}
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('birth date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="birth_date"
                                        value={petCreateForm.birth_date}
                                        onChange={handlePetCreateChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">{t('gender')}</Form.Label>
                                    <Form.Select
                                        name="male"
                                        value={petCreateForm.gender}
                                        onChange={handlePetCreateChange}
                                    >
                                        <option value="1">{t('Male')}</option>
                                        <option value="0">{t('Female')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">{t('status')}</Form.Label>
                                    <Form.Select
                                        name="is_dead"
                                        value={petCreateForm.is_dead ? "1" : "0"}
                                        onChange={(e) => {
                                            const newValue = e.target.value === "1";
                                            setPetCreateForm(prev => ({ ...prev, is_dead: newValue }));
                                            if (petCreateErrors.is_dead) setPetCreateErrors(prev => ({ ...prev, is_dead: '' }));
                                        }}
                                    >
                                        <option value="0">{t('Alive')}</option>
                                        <option value="1">{t('Deceased')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            type="button" className="btn btn-secondary" onClick={() => setPetCreateModal(false)}>
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={petCreateLoading}>
                            {petCreateLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('saving')}...</> : t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}